import { Routes, Route, Navigate } from 'react-router-dom';
import AppLayout from '@/components/layout/AppLayout';
import ProtectedRoute from '@/components/layout/ProtectedRoute';
import { useVersionCheck } from '@/hooks/useVersionCheck';

// Public pages
import LoginPage from '@/pages/LoginPage';
import AcceptInvitePage from '@/pages/AcceptInvitePage';
import ForgotPasswordPage from '@/pages/ForgotPasswordPage';
import ResetPasswordPage from '@/pages/ResetPasswordPage';

// Protected pages
import DashboardPage from '@/pages/DashboardPage';
import EntityListPage from '@/pages/EntityListPage';
import EntityCashFlowPage from '@/pages/EntityCashFlowPage';
import ValidationBoardPage from '@/pages/ValidationBoardPage';
import AuditPage from '@/pages/AuditPage';

// Account pages
import ChangePasswordPage from '@/pages/ChangePasswordPage';
import HelpPage from '@/pages/HelpPage';

// Admin pages
import CfCodeAdminPage from '@/pages/admin/CfCodeAdminPage';
import EntitiesAdminPage from '@/pages/admin/EntitiesAdminPage';
import StatementLayoutAdminPage from '@/pages/admin/StatementLayoutAdminPage';
import UsersAdminPage from '@/pages/admin/UsersAdminPage';
import ExchangeRunsPage from '@/pages/admin/ExchangeRunsPage';
import EngagementPage from '@/pages/admin/EngagementPage';

export default function App() {
  // Auto-reload an open tab when a newer build is deployed (cache-busting).
  useVersionCheck();

  return (
    <Routes>
      {/* ── Public routes ─────────────────────────────────────────────────── */}
      <Route path="/login" element={<LoginPage />} />
      <Route path="/accept-invite" element={<AcceptInvitePage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />

      {/* ── Protected routes (all behind auth guard + sidebar layout) ───── */}
      <Route element={<AppLayout />}>
        {/* Home is the Validation Board; the system dashboard lives under Admin. */}
        <Route path="/" element={<Navigate to="/validation" replace />} />
        <Route path="/entities" element={<EntityListPage />} />
        <Route path="/entities/:entityId/cashflow/:period" element={<EntityCashFlowPage />} />
        <Route path="/validation" element={<ValidationBoardPage />} />
        <Route path="/account/change-password" element={<ChangePasswordPage />} />
        <Route path="/help" element={<HelpPage />} />

        {/* Audit — admin and group finance only */}
        <Route element={<ProtectedRoute requiredRoles={['ADMIN', 'GROUP_FINANCE']} />}>
          <Route path="/audit" element={<AuditPage />} />
        </Route>

        {/* Admin-only routes */}
        <Route element={<ProtectedRoute requiredRoles={['ADMIN']} />}>
          <Route path="/admin/dashboard" element={<DashboardPage />} />
          <Route path="/admin/cf-codes" element={<CfCodeAdminPage />} />
          <Route path="/admin/entities" element={<EntitiesAdminPage />} />
          <Route path="/admin/layout" element={<StatementLayoutAdminPage />} />
          <Route path="/admin/users" element={<UsersAdminPage />} />
          <Route path="/admin/exchange-runs" element={<ExchangeRunsPage />} />
          <Route path="/admin/engagement" element={<EngagementPage />} />
        </Route>
      </Route>

      {/* Catch-all → Validation Board (home) */}
      <Route path="*" element={<Navigate to="/validation" replace />} />
    </Routes>
  );
}
